/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `EventCreation` for the Caintra Connect frontend.
 * @module pages/EventCreation
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/EventCreation.module.css";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import { AppContext } from "../context/AppContext";
import { submitEvent, initializeEventCreation } from "../logic/eventCreation";
import { canManageRegistrationEvents } from "../utils/accessControl";
import {
  isEventCreationSaveDisabled,
  toggleEventAudiencia,
} from "../utils/eventCreation/eventCreationValidation.js";
import EventCreationBannerSection from "../components/EventCreation/EventCreationBannerSection.jsx";
import EventCreationDetailsSection from "../components/EventCreation/EventCreationDetailsSection.jsx";
import EventCreationConfigurationSection from "../components/EventCreation/EventCreationConfigurationSection.jsx";
import EventCreationSaveSection from "../components/EventCreation/EventCreationSaveSection.jsx";
import { buildEventCreationSubmitPayload } from "../utils/eventCreation/eventCreationSubmitPayload.js";
import {
  readEventBannerAsDataUrl,
  validateEventBannerFile,
} from "../utils/eventCreation/eventBannerUpload.js";
import { getEventCreationCategorySelectStyles } from "../utils/eventCreation/eventCreationCategorySelectStyles.js";
import useRequireAccess from "../hooks/useRequireAccess";

import { CircularProgress, Backdrop } from "@mui/material";

import { ReactComponent as TitleIcon } from "../assets/icons/EventCreation/TitleIcon.svg";
import { ReactComponent as ChevronLeftIcon } from "../assets/icons/EventCreation/ChevronLeftIcon.svg";

import { use, useRef, useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router";
import { useTranslation } from "react-i18next";

const EventCreation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { changePermissions } = use(AppContext);
  const { allowed } = useRequireAccess(canManageRegistrationEvents);

  const { eventId } = useParams();
  const [formData, setFormData] = useState({
    imagenBanner: null,
    nombre: "",
    descripcion: "",
    ubicacion: "",
    costoSocios: null,
    costoNoSocios: null,
    cupoMaximo: null,
    categoria: null,
    audiencia: null,
    hora: null,
    duracion: null,
    fechaEvento: null,
    fechaDisponibleSocios: null,
    fechaDisponibleNoSocios: null,
    fechaLimiteRegistro: null,
    interesesComercialesRequerido: false,
    expectativasRequerido: false,
    activo: false,
  });
  const [showError, setShowError] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [emailRequired, setEmailRequired] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imageChanged, setImageChanged] = useState(false);
  const [imageFormat, setImageFormat] = useState(null);
  const [imageName, setImageName] = useState(null);
  const isEditMode = Boolean(eventId);

  const selectCategoryRef = useRef(null);
  const imageRef = useRef(null);
  const onDragEnter = () => imageRef.current?.classList.add(styles.dragOver);
  const onDragLeave = () => imageRef.current?.classList.remove(styles.dragOver);
  const onDrop = () => imageRef.current?.classList.remove(styles.dragOver);

  useEffect(() => {
    if (!allowed) {
      return;
    }
    if (!location.state?.manageEventsFlow) {
      navigate("/manageEvents", { replace: true });
      return;
    }

    const initializeComponent = async () => {
      const token = localStorage.getItem("token");
      logger.info("eventId: ", { data: eventId });

      if (!token || token.trim() === "undefined") {
        logger.info(
          "Error trying to get the token to retrieve the company data.",
        );
        return;
      }

      const userId = localStorage.getItem("userId");

      await initializeEventCreation(
        eventId,
        userId,
        changePermissions,
        navigate,
        setFormData,
        setLoading,
      );
    };

    initializeComponent();
  }, [allowed, navigate, location.state, location.pathname]);

  const handleImageChange = async (event) => {
    const validation = validateEventBannerFile(event.target.files[0]);
    if (!validation.valid) {
      if (validation.reason === "type") {
        setError(t("pages.buyerCompanyForm.logoFileTypeError"));
      } else if (validation.reason === "size") {
        setError(t("pages.buyerCompanyForm.logoFileSizeError"));
      }
      setImageChanged(false);
      setFormData((prev) => ({ ...prev, imagenBanner: null }));
      setImageName(null);
      setImageFormat(null);
      return;
    }

    try {
      setError("");
      const banner = await readEventBannerAsDataUrl(validation.file);
      setImageChanged(true);
      setFormData((prev) => ({ ...prev, imagenBanner: banner.dataUrl }));
      setImageName(banner.name);
      setImageFormat(banner.format);
      setHasChanges(true);
    } catch (readError) {
      logger.error("Error reading event banner:", { err: sanitizeError(readError) });
      setError(t("pages.buyerCompanyForm.logoFileTypeError"));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowError(false);
    setSaving(true);

    try {
      const eventData = buildEventCreationSubmitPayload({
        formData,
        imageChanged,
        imageName,
        imageFormat,
        emailRequired,
        userId: localStorage.getItem("userId"),
      });

      const result = await submitEvent(
        eventData,
        eventId,
        navigate,
        setSaving,
        setHasChanges,
      );
      if (result?.ok === false) {
        setShowError(true);
      }
    } catch (err) {
      logger.error("Error submitting event:", { err: sanitizeError(err) });
      setShowError(true);
      setSaving(false);
    }
  };

  const checkSaveDisabled = () =>
    isEventCreationSaveDisabled({
      hasChanges,
      loading,
      saving,
      formData,
    });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === "fechaEvento" || name === "hora" || name === "ubicacion") {
      setEmailRequired(true);
    }
    setHasChanges(true);
  };

  const handleSelectChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [selectedOption.name]: selectedOption.value,
    }));
    setHasChanges(true);
  };

  const handleCategorySelectChange = (selectedOption) => {
    if (selectedOption) {
      selectedOption.name = "categoria";
      handleSelectChange(selectedOption);
    }
  };

  const categorySelectStyles = useMemo(
    () => getEventCreationCategorySelectStyles(showError, formData.categoria),
    [showError, formData.categoria],
  );

  const handleCheckboxChange = (e, fieldName) => {
    logger.info("Checkbox changed: ", { data: [fieldName, e.target.checked] });
    setFormData((prev) => ({
      ...prev,
      [fieldName]: e.target.checked,
    }));
    setHasChanges(true);
  };

  /** @param {"socios"|"noSocios"} which */
  const handleAudienciaCheckboxChange = (which) => {
    setFormData((prev) => ({
      ...prev,
      audiencia: toggleEventAudiencia(prev.audiencia, which),
    }));
    setHasChanges(true);
  };

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        {/* Back navigation */}
        <div className={styles.backNavigation}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <ChevronLeftIcon />
            <span>{t("common.goBack")}</span>
          </button>
        </div>

        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <TitleIcon className={styles.titleIcon} />
            <h1 className={styles.title}>{t("pages.eventCreation.header")}</h1>
          </div>
        </div>
      </div>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={eventId && loading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>

      <div className={styles.formContainer}>
        <form onSubmit={handleSubmit} className={styles.eventForm}>
          <div className={styles.formGroups}>
            <div className={styles.formGroupOne}>
              <EventCreationBannerSection
                t={t}
                styles={styles}
                isEditMode={isEditMode}
                formData={formData}
                error={error}
                imageRef={imageRef}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onImageChange={handleImageChange}
                onAudienciaCheckboxChange={handleAudienciaCheckboxChange}
                onActivoChange={(checked) => {
                  setHasChanges(true);
                  setFormData((prev) => ({ ...prev, activo: checked }));
                }}
              />
              <EventCreationDetailsSection
                t={t}
                styles={styles}
                formData={formData}
                showError={showError}
                selectCategoryRef={selectCategoryRef}
                categorySelectStyles={categorySelectStyles}
                onInputChange={handleInputChange}
                onCategorySelectChange={handleCategorySelectChange}
              />
            </div>
            <EventCreationConfigurationSection
              t={t}
              styles={styles}
              formData={formData}
              onCheckboxChange={handleCheckboxChange}
            />
          </div>
          <EventCreationSaveSection
            t={t}
            styles={styles}
            loading={loading}
            saving={saving}
            isSaveDisabled={checkSaveDisabled()}
          />
        </form>
      </div>
    </div>
  );
};

export default EventCreation;
